/**
 * Aligera las fotos que el cliente adjunta desde el móvil antes de enviarlas.
 * Una foto de móvil suele pasar de 5 MB; para leer un DNI o una escritura basta con ~2000 px.
 * Se redimensiona en el navegador (nada sale del dispositivo) y se guarda como JPEG.
 * Si algo falla, o el resultado no es más pequeño, se devuelve el archivo original.
 */

const UMBRAL_BYTES = 1.2 * 1024 * 1024 // por debajo no se toca
const LADO_MAX = 2200
const LADO_MAX_REINTENTO = 1800
const OBJETIVO_BYTES = 1.8 * 1024 * 1024
// Último intento para fotos muy detalladas que aún superarían el límite de subida (5 MB, ADJUNTOS)
const LADO_MAX_ULTIMO = 1400
const MAX_BYTES_ULTIMO = 4 * 1024 * 1024

export function esImagen(f: File) {
  return /^image\/(jpeg|png)$/.test(f.type) || /\.(jpe?g|png)$/i.test(f.name)
}

async function decodificar(f: File): Promise<CanvasImageSource & { width: number; height: number }> {
  // createImageBitmap respeta la orientación EXIF (las fotos del móvil no salen giradas)
  if (typeof createImageBitmap === 'function') {
    try { return await createImageBitmap(f, { imageOrientation: 'from-image' } as any) } catch { /* se prueba con <img> */ }
  }
  const url = URL.createObjectURL(f)
  try {
    const img = new Image()
    img.decoding = 'async'
    img.src = url
    await img.decode()
    return img as any
  } finally {
    URL.revokeObjectURL(url)
  }
}

function aJpeg(fuente: CanvasImageSource & { width: number; height: number }, ladoMax: number, calidad: number): Promise<Blob | null> {
  const escala = Math.min(1, ladoMax / Math.max(fuente.width, fuente.height))
  const ancho = Math.max(1, Math.round(fuente.width * escala))
  const alto = Math.max(1, Math.round(fuente.height * escala))
  const canvas = document.createElement('canvas')
  canvas.width = ancho
  canvas.height = alto
  const ctx = canvas.getContext('2d')
  if (!ctx) return Promise.resolve(null)
  ctx.fillStyle = '#ffffff' // los PNG con transparencia quedan sobre blanco
  ctx.fillRect(0, 0, ancho, alto)
  ctx.drawImage(fuente, 0, 0, ancho, alto)
  return new Promise((ok) => canvas.toBlob((b) => ok(b), 'image/jpeg', calidad))
}

export async function aligerarImagen(f: File): Promise<File> {
  if (!process.client || !esImagen(f) || f.size <= UMBRAL_BYTES) return f
  try {
    const fuente = await decodificar(f)
    let blob = await aJpeg(fuente, LADO_MAX, 0.82)
    if (blob && blob.size > OBJETIVO_BYTES) blob = await aJpeg(fuente, LADO_MAX_REINTENTO, 0.72)
    if (blob && blob.size > MAX_BYTES_ULTIMO) blob = await aJpeg(fuente, LADO_MAX_ULTIMO, 0.6)
    ;(fuente as any).close?.()
    if (!blob || blob.size >= f.size) return f
    const nombre = f.name.replace(/\.(jpe?g|png)$/i, '') + '.jpg'
    return new File([blob], nombre, { type: 'image/jpeg', lastModified: Date.now() })
  } catch (e) {
    console.warn('[SolicitudWeb] No se pudo aligerar la imagen; se envía la original', e)
    return f
  }
}

/** Prepara un adjunto antes de comprobar su tamaño: aligera las fotos y reduce los PDF que pasen de maxBytes. */
export async function aligerarAdjunto(f: File, maxBytes: number): Promise<File> {
  if (esImagen(f)) return aligerarImagen(f)
  const esPdf = f.type === 'application/pdf' || /\.pdf$/i.test(f.name)
  if (!process.client || !esPdf || f.size <= maxBytes) return f
  const { aligerarPdf } = await import('./solicitudPdf')
  return aligerarPdf(f, maxBytes)
}
